import React, { useState } from "react";
import { AVAILABLE_PRICING } from "../../constants/constants";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createCheckoutSession } from "../../features/transaction/transactionAPI";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

function CheckoutForm() {
  const [amount] = useState(5);
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    amount: Yup.mixed()
      .required("Please select or enter an amount")
      .test("valid-amount", "Amount must be at least $1", (value) => {
        if (value === "" || value === undefined) return false;
        return Number(value) >= 1;
      })
      .test("max-amount", "Maximum amount is $10,000", (value) => {
        if (!value) return false;
        return Number(value) <= 10000;
      }),
  });

  const formik = useFormik({
    initialValues: { amount },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const stripeSession = await createCheckoutSession({
          amount: values.amount,
          isOnboardingCompleted: user?.isOnboardingCompleted
        });
        navigate(stripeSession.url);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const checkTransactions = () => {
    navigate("/billing");
  };

  const manageSubscription = () => {
    navigate("/pricing");
  };

  const planDetails = () => {
    navigate("/plan-details");
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="container py-12">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-xl-5">
            <div
              className="card border-0 shadow-lg"
              style={{
                borderRadius: "16px",
              }}
            >
              <div className="card-body p-5 d-flex flex-column">
                <div className="d-flex justify-content-center gap-2 mb-5">
                  <button
                    onClick={() => checkTransactions()}
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill px-3"
                  >
                    <i className="wb webconnect-receipt"></i>
                    Transactions
                  </button>

                  <button
                    onClick={() => manageSubscription()}
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill px-3"
                  >
                    <i className="wb webconnect-gear"></i>
                    Manage Subscription
                  </button>

                  <button
                    onClick={() => planDetails()}
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill px-3"
                  >
                    <i className="wb webconnect-credit-card"></i>
                    Plan Details
                  </button>
                </div>
                <div className="text-center">
                  <h2 className="fw-bold mb-2">Add Credits</h2>
                  <p className="mb-0" style={{ fontSize: "0.95rem" }}>
                    Enter the amount you'd like to add
                  </p>
                </div>

                <div
                  className="d-flex flex-column justify-content-center align-items-center text-center flex-grow-1"
                  style={{ minHeight: "230px" }}
                >
                  <h1 className="fw-bold mb-4 d-flex align-items-start justify-content-center">
                    <span
                      style={{
                        fontSize: "1.8rem",
                        marginTop: "1.4rem",
                        marginRight: "5px",
                        opacity: 0.7,
                      }}
                    >
                      $
                    </span>
                    <span
                      style={{
                        fontSize: "3.5rem",
                        lineHeight: 1,
                      }}
                    >
                      {formik.values.amount || 0}
                    </span>
                  </h1>
                  <div className="d-flex flex-wrap justify-content-center gap-2 mb-3">
                    {AVAILABLE_PRICING.map((price) => {
                      const isActive =
                        Number(formik.values.amount) === price.amount;

                      return (
                        <span
                          key={price.id}
                          onClick={() => {
                            formik.setFieldValue("amount", price.amount, true);
                            formik.setFieldTouched("amount", true, false);
                          }}
                          className="badge rounded-pill px-3 py-2"
                          style={{
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            background: isActive
                              ? "rgba(144, 255, 180, 0.25)"
                              : "rgba(144, 255, 180, 0.10)",
                            border: isActive
                              ? "1px solid rgba(144, 255, 180, 0.8)"
                              : "1px solid rgba(144, 255, 180, 0.3)",
                            color: "var(--phoenix-success-text-emphasis)",
                            transition: "all 0.2s ease",
                          }}
                        >
                          ${price.amount}
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="fw-bold mb-0">Custom amount</label>
                  </div>

                  <div className="input-group">
                    <span
                      className="input-group-text"
                      style={{
                        color: "#9ca3af",
                      }}
                    >
                      $
                    </span>

                    <input
                      type="text"
                      name="amount"
                      value={formik.values.amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!/^\d*$/.test(value)) return;
                        formik.setFieldValue("amount", value);
                      }}
                      onBlur={formik.handleBlur}
                      placeholder="Enter amount"
                      maxLength={5}
                      autoComplete="off"
                      className={`form-control ${
                        formik.touched.amount && formik.errors.amount
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                  </div>

                  {formik.touched.amount && formik.errors.amount && (
                    <div
                      className="text-danger mt-2"
                      style={{ fontSize: "0.90rem" }}
                    >
                      {formik.errors.amount}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formik.isSubmitting || !formik.values.amount}
                    className="btn btn-primary gap-2 py-3 w-100 mt-4"
                    style={{ fontSize: "larger" }}
                  >
                    {formik.isSubmitting ? (
                      "Processing..."
                    ) : (
                      <>Add ${formik.values.amount || 0}</>
                    )}
                  </button>
                </div>

                <p
                  className=" mb-0 text-center"
                  style={{ fontSize: "0.95rem" }}
                >
                  Secure checkout • No hidden fees • Powered by Stripe
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

export default CheckoutForm;
